import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { generateUUID } from "@/lib/utils/uuid";
import { normalizeStoragePath } from "@/lib/utils/storage";
import { useTranslation } from "react-i18next";
import {
  DEFAULT_DOCUMENT_TYPE_NAME,
  MAX_DOCUMENT_FILE_SIZE_MB,
  getDocumentTitleFromFilename,
  isAcceptedDocumentFile,
  isFileWithinDocumentSizeLimit,
  normalizeDocumentTypeName,
} from "@/lib/pet-documents/document-upload";

const normalizeUploadedDocumentDate = (value?: Date | string | null) => {
  if (!value) return null;
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
  const date = typeof value === "string" ? new Date(value) : value;
  return Number.isNaN(date.getTime()) ? null : date.toISOString().slice(0, 10);
};

// Upload de foto do pet
export const useUploadPetPhoto = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useTranslation("patients");

  return useMutation({
    mutationFn: async ({ petId, file }: { petId: string; file: File; suppressToast?: boolean }) => {
      // Validar tipo de arquivo
      const validTypes = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
      if (!validTypes.includes(file.type)) {
        throw new Error(t("phase8.upload.errors.invalidImageType"));
      }

      // Validar tamanho (5MB)
      if (file.size > 5 * 1024 * 1024) {
        throw new Error(t("phase8.upload.errors.imageTooLarge"));
      }

      // Gerar nome único para o arquivo com UUID
      const fileExt = file.name.split(".").pop();
      const uuid = generateUUID();
      const fileName = `pets/${petId}/${uuid}.${fileExt}`;

      // Upload para storage
      const { error: uploadError } = await supabase.storage
        .from("pet-photos")
        .upload(fileName, file, {
          upsert: true,
        });

      if (uploadError) throw uploadError;

      // Obter URL pública
      const { data: urlData } = supabase.storage
        .from("pet-photos")
        .getPublicUrl(fileName);

      // Verificar se já existe uma foto para este pet (incluindo soft deleted)
      const { data: existingPhoto } = await supabase
        .from("pet_photos")
        .select("id, filename")
        .eq("pet_id", petId)
        .maybeSingle();

      if (existingPhoto) {
        // Se existe, fazer UPDATE do registro existente
        // Deletar o arquivo antigo do storage se existir e for diferente
        if (existingPhoto.filename && existingPhoto.filename !== fileName) {
          await supabase.storage.from("pet-photos").remove([existingPhoto.filename]);
        }

        // Atualizar o registro existente
        const { error: updateError } = await supabase
          .from("pet_photos")
          .update({
            filename: fileName,
            original_name: file.name,
            file_path: urlData.publicUrl,
            mime_type: file.type,
            file_size: file.size,
            deleted_at: null, // Garantir que não está soft deleted
            updated_at: new Date().toISOString(),
          })
          .eq("id", existingPhoto.id);

        if (updateError) throw updateError;
      } else {
        // Se não existe, inserir nova foto
        const { error: insertError } = await supabase.from("pet_photos").insert({
          pet_id: petId,
          filename: fileName,
          original_name: file.name,
          file_path: urlData.publicUrl,
          mime_type: file.type,
          file_size: file.size,
        });

        if (insertError) throw insertError;
      }

      return urlData.publicUrl;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["pet", variables.petId] });
    },
    onError: (error: Error, variables) => {
      if (!variables.suppressToast) {
        toast({
          title: t("phase8.upload.photoUploadErrorTitle"),
          description: error.message,
          variant: "destructive",
        });
      }
    },
  });
};

// Upload de anexo do pet
export const useUploadPetAttachment = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useTranslation("patients");

  return useMutation({
    mutationFn: async ({
      petId,
      file,
      name,
      type,
      description,
      documentDate,
    }: {
      petId: string;
      file: File;
      name?: string;
      type?: string;
      description?: string;
      documentDate?: Date | string | null;
      suppressToast?: boolean;
    }) => {
      if (!isFileWithinDocumentSizeLimit(file)) {
        throw new Error(t("petAttachments.tooLargeLimit", { size: MAX_DOCUMENT_FILE_SIZE_MB }));
      }

      if (!isAcceptedDocumentFile(file)) {
        throw new Error(t("petAttachments.unsupportedType"));
      }

      const documentName = name?.trim() || getDocumentTitleFromFilename(file.name) || file.name;
      const documentType = normalizeDocumentTypeName(type ?? DEFAULT_DOCUMENT_TYPE_NAME);

      // Gerar nome único para o arquivo com UUID
      const fileExt = file.name.split(".").pop();
      const uuid = generateUUID();
      const fileName = `pets/${petId}/${uuid}.${fileExt}`;

      // Upload para storage
      const { error: uploadError } = await supabase.storage
        .from("pet-attachments")
        .upload(fileName, file);

      if (uploadError) throw uploadError;

      // Salvar registro no banco
      const { error: dbError } = await supabase.from("pet_attachments").insert({
        pet_id: petId,
        filename: fileName,
        file_name: documentName,
        original_name: file.name,
        file_path: fileName,
        mime_type: file.type,
        file_size: file.size,
        type: documentType,
        category: documentType,
        description: description || null,
        exam_date: normalizeUploadedDocumentDate(documentDate),
        storage_bucket: "pet-attachments",
      });

      if (dbError) throw dbError;

      return fileName;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["pet", variables.petId] });
    },
    onError: (error: Error, variables) => {
      if (!variables.suppressToast) {
        toast({
          title: t("phase8.upload.attachmentUploadErrorTitle"),
          description: error.message,
          variant: "destructive",
        });
      }
    },
  });
};

// Deletar foto do pet
export const useDeletePetPhoto = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useTranslation("patients");

  return useMutation({
    mutationFn: async (petId: string) => {
      // Buscar foto atual
      const { data: photo } = await supabase
        .from("pet_photos")
        .select("filename")
        .eq("pet_id", petId)
        .single();

      if (photo) {
        // Deletar do storage
        await supabase.storage.from("pet-photos").remove([photo.filename]);

        // Deletar do banco
        const { error } = await supabase
          .from("pet_photos")
          .delete()
          .eq("pet_id", petId);

        if (error) throw error;
      }
    },
    onSuccess: (_, petId) => {
      queryClient.invalidateQueries({ queryKey: ["pet", petId] });
      toast({
        title: t("phase8.upload.photoDeletedTitle"),
      });
    },
    onError: (error: Error) => {
      toast({
        title: t("phase8.upload.photoDeleteErrorTitle"),
        description: error.message,
        variant: "destructive",
      });
    },
  });
};

// Deletar anexo do pet
export const useDeletePetAttachment = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useTranslation("patients");

  return useMutation({
    mutationFn: async (attachmentId: string) => {
      // Buscar anexo
      const { data: attachment } = await supabase
        .from("pet_attachments")
        .select("filename, file_path, storage_bucket, pet_id")
        .eq("id", attachmentId)
        .single();

      if (attachment) {
        const storagePath =
          normalizeStoragePath(attachment.file_path, attachment.storage_bucket) ??
          normalizeStoragePath(attachment.filename, attachment.storage_bucket) ??
          attachment.filename ??
          attachment.file_path;

        if (storagePath.startsWith("http")) {
          throw new Error(t("phase8.upload.errors.invalidRemovalPath"));
        }

        // Deletar do storage
        await supabase.storage
          .from(attachment.storage_bucket ?? "pet-attachments")
          .remove([storagePath]);

        // Soft delete no banco
        const { error } = await supabase
          .from("pet_attachments")
          .update({ deleted_at: new Date().toISOString() })
          .eq("id", attachmentId);

        if (error) throw error;

        return attachment.pet_id;
      }
    },
    onSuccess: (petId) => {
      if (petId) {
        queryClient.invalidateQueries({ queryKey: ["pet", petId] });
      }
      toast({
        title: t("phase8.upload.attachmentDeletedTitle"),
      });
    },
    onError: (error: Error) => {
      toast({
        title: t("phase8.upload.attachmentDeleteErrorTitle"),
        description: error.message,
        variant: "destructive",
      });
    },
  });
};
